import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { SEMANTIC, UI } from '../../styles/theme';
import BackButton from '../common/BackButton';
import KycProgressBar from '../KycProgressBar';
import API from '../../api';

// Normalizza una stringa per il confronto firma:
// minuscolo, spazi multipli collassati, trim
function normalizeName(s) {
  return (s || '').toLowerCase().replace(/\s+/g, ' ').trim();
}

// ======================================================
// Sostituisce i segnaposto {{key}} con i valori utente
// ======================================================
function fillTemplate(html, user) {
  const today = new Date().toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
  return html
    .replace(/\{\{first_name\}\}/g, user.first_name || '—')
    .replace(/\{\{last_name\}\}/g,  user.last_name  || '—')
    .replace(/\{\{tax_code\}\}/g,   user.tax_code   || '—')
    .replace(/\{\{residence_city\}\}/g, user.residence_city || '—')
    .replace(/\{\{email\}\}/g,      user.email      || '—')
    .replace(/\{\{date\}\}/g,       today);
}

// ======================================================
// Template contratto (renderizza HTML dal DB)
// ======================================================
const ContractTemplate = React.forwardRef(({ html }, ref) => (
  <div
    ref={ref}
    dangerouslySetInnerHTML={{ __html: html }}
  />
));

// ======================================================
// MAIN COMPONENT
// ======================================================
const ContractSignature = () => {
  const [accepted, setAccepted] = useState(false);
  const [signature, setSignature] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState({ first_name: '', last_name: '', tax_code: '', residence_city: '', email: '' });
  const [templateHtml, setTemplateHtml] = useState('');
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId');
  const { t } = useTranslation();
  const contractRef = useRef(null);

  useEffect(() => {
    API.get(`/kyc/user-info?user_id=${userId}`)
      .then(res => setUser(res.data))
      .catch(() => {});

    API.get('/contract-template')
      .then(res => setTemplateHtml(res.data.content || ''))
      .catch(() => {});
  }, [userId]);

  const renderedHtml = fillTemplate(templateHtml, user);

  // Genera il PDF e restituisce { pdf, base64 }
  const generatePDF = async () => {
    const canvas = await html2canvas(contractRef.current, {
      scale: 2,
      backgroundColor: '#ffffff',
      useCORS: true,
    });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pdfW = pdf.internal.pageSize.getWidth();
    const pdfH = (canvas.height * pdfW) / canvas.width;
    pdf.addImage(imgData, 'PNG', 0, 0, pdfW, pdfH);
    const base64 = pdf.output('datauristring').split(',')[1];
    return { pdf, base64 };
  };

  const handleDownload = async () => {
    try {
      const { pdf } = await generatePDF();
      pdf.save('contratto_kuesto.pdf');
    } catch {
      setError('Errore nella generazione del PDF.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!accepted) { setError(t('kyc.contract.error_accept')); return; }
    if (!signature.trim()) { setError(t('kyc.contract.error_signature')); return; }

    const expectedName = `${user.first_name} ${user.last_name}`;
    if (normalizeName(signature) !== normalizeName(expectedName)) {
      setError(t('kyc.contract.error_signature_mismatch', { name: expectedName }));
      return;
    }
    setLoading(true);
    try {
      // 1. Generate PDF while DOM is still mounted
      const { base64 } = await generatePDF();
      // 2. Save signature — fast (no PDF in payload)
      await API.post('/kyc/contract-signature', {
        user_id: userId,
        signature,
        signed_at: new Date().toISOString(),
      });
      // 3. Navigate immediately
      navigate('/kyc/mifid');
      // 4. Fire-and-forget: upload PDF + trigger email (component unmounted, no setState here)
      API.post('/kyc/contract-pdf', { user_id: userId, pdf_base64: base64 }).catch(() => {});
    } catch {
      setLoading(false);
      setError(t('kyc.contract.error_save'));
    }
  };

  return (
    <div style={{ backgroundColor: SEMANTIC.backgroundApp, minHeight: '100vh', display: 'flex', flexDirection: 'column', padding: UI.spacing.lg }}>
      <BackButton to="/kyc/fatca" />
      <div style={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: UI.spacing.xl }}>
          <img src={`${process.env.PUBLIC_URL}/Logo_Kuesto.png`} alt="Kuesto Logo" style={{ width: '120px' }} />
        </div>
        <KycProgressBar currentStep={9} totalSteps={10} />
        <div style={{ backgroundColor: SEMANTIC.backgroundCard, borderRadius: UI.borderRadius.md, padding: UI.spacing.xl, boxShadow: UI.shadow.card }}>
          <h2 style={{ color: SEMANTIC.actionBuy, marginBottom: UI.spacing.md, fontSize: UI.fontSize.title, fontWeight: UI.fontWeight.bold, textAlign: 'center' }}>
            {t('kyc.contract.title')}
          </h2>
          <p style={{ color: SEMANTIC.textSecondary, marginBottom: UI.spacing.xl, textAlign: 'center', fontSize: UI.fontSize.md }}>
            {t('kyc.contract.description')}
          </p>

          {error && (
            <div style={{ backgroundColor: 'rgba(255,77,77,0.1)', border: `2px solid ${SEMANTIC.actionSell}`, borderRadius: UI.borderRadius.sm, padding: UI.spacing.md, marginBottom: UI.spacing.lg, color: SEMANTIC.actionSell, textAlign: 'center' }}>
              {error}
            </div>
          )}

          {/* Anteprima contratto */}
          <div style={{ border: '1px solid rgba(255,255,255,0.12)', borderRadius: UI.borderRadius.sm, marginBottom: UI.spacing.lg, maxHeight: '500px', overflowY: 'auto' }}>
            {templateHtml
              ? <ContractTemplate ref={contractRef} html={renderedHtml} />
              : <div style={{ padding: 40, textAlign: 'center', color: SEMANTIC.textSecondary }}>Caricamento contratto...</div>
            }
          </div>

          {/* Download */}
          <div style={{ textAlign: 'center', marginBottom: UI.spacing.xl }}>
            <button
              onClick={handleDownload}
              style={{ padding: `${UI.spacing.sm} ${UI.spacing.lg}`, backgroundColor: 'transparent', color: SEMANTIC.actionBuy, border: `2px solid ${SEMANTIC.actionBuy}`, borderRadius: UI.borderRadius.sm, fontSize: UI.fontSize.md, fontWeight: UI.fontWeight.bold, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: UI.spacing.sm }}
            >
              <i className="bi bi-download" style={{ fontSize: '18px' }}></i> {t('kyc.contract.download_pdf')}
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ backgroundColor: SEMANTIC.backgroundCard, padding: UI.spacing.lg, borderRadius: UI.borderRadius.sm, marginBottom: UI.spacing.lg, display: 'flex', alignItems: 'flex-start', gap: UI.spacing.md, border: '1px solid rgba(255,255,255,0.08)' }}>
              <input type="checkbox" id="acceptContract" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} style={{ width: '20px', height: '20px', cursor: 'pointer', accentColor: SEMANTIC.actionBuy, marginTop: '2px' }} />
              <label htmlFor="acceptContract" style={{ color: SEMANTIC.textPrimary, fontSize: UI.fontSize.md, cursor: 'pointer', flex: 1 }}>
                <strong style={{ color: SEMANTIC.actionBuy }}>*</strong> {t('kyc.contract.accept_label')}
              </label>
            </div>

            <div style={{ marginBottom: UI.spacing.xl }}>
              <label htmlFor="signature" style={{ color: SEMANTIC.textPrimary, display: 'block', marginBottom: UI.spacing.sm, fontSize: UI.fontSize.md, fontWeight: UI.fontWeight.bold }}>
                {t('kyc.contract.signature_label')}
              </label>
              <input
                type="text"
                id="signature"
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                placeholder={t('kyc.contract.signature_placeholder')}
                disabled={!accepted}
                style={{ width: '100%', padding: UI.spacing.md, backgroundColor: SEMANTIC.backgroundCard, color: accepted ? SEMANTIC.textPrimary : SEMANTIC.textDisabled, border: `2px solid ${accepted ? SEMANTIC.actionBuy : 'rgba(255,255,255,0.12)'}`, borderRadius: UI.borderRadius.sm, fontSize: UI.fontSize.lg, fontFamily: 'cursive', fontStyle: 'italic' }}
              />
              <p style={{ color: SEMANTIC.textSecondary, fontSize: UI.fontSize.sm, marginTop: UI.spacing.sm }}>
                {t('kyc.contract.signature_note')}{' '}
                {user.first_name && (
                  <span style={{ color: SEMANTIC.actionBuy, fontWeight: 600 }}>
                    ({user.first_name} {user.last_name})
                  </span>
                )}
              </p>
            </div>

            <div style={{ backgroundColor: 'rgba(0,255,136,0.05)', border: `1px solid ${SEMANTIC.actionBuy}`, borderRadius: UI.borderRadius.sm, padding: UI.spacing.md, marginBottom: UI.spacing.xl }}>
              <p style={{ color: SEMANTIC.textSecondary, fontSize: UI.fontSize.sm, margin: 0 }}>
                {t('kyc.contract.info_note')}
              </p>
            </div>

            <button
              type="submit"
              disabled={!accepted || !signature.trim() || loading}
              style={{ width: '100%', padding: UI.spacing.md, backgroundColor: accepted && signature.trim() && !loading ? SEMANTIC.ctaPrimary : SEMANTIC.backgroundDisabled, color: accepted && signature.trim() && !loading ? SEMANTIC.ctaPrimaryText : SEMANTIC.textDisabled, border: 'none', borderRadius: UI.borderRadius.sm, fontSize: UI.fontSize.lg, fontWeight: UI.fontWeight.bold, cursor: accepted && signature.trim() && !loading ? 'pointer' : 'not-allowed' }}
            >
              {loading ? t('kyc.contract.saving') : t('kyc.contract.button')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContractSignature;
